import * as THREE from "three";
import {
  FIXED_DT,
  FOOD_TYPES,
  HUNGER_DRAIN_PER_SECOND,
  HUNGER_MAX,
  LEVEL_XP_REQUIREMENTS,
  MAX_FRAME_DT,
  MAX_LEVEL,
  SHOO_HUNGER_PENALTY,
  STAMINA_PER_LEVEL,
  SPEED_PER_LEVEL,
  XP_PER_FLY_SECOND,
  XP_PER_FOOD_POINT,
  XP_PER_SPRINT_SECOND,
  makeSeed,
  seededRng,
} from "@palomitas/shared";
import type { FoodType } from "@palomitas/shared";
import { LocalTransport } from "../net/transport";
import type { HudBinding } from "../ui/hud.ts";
import { createScene } from "./scene";
import type { SceneBundle } from "./scene";
import { buildWorld } from "./world";
import type { WorldBuildResult } from "./world";
import { PIGEON_SPAWN, Pigeon } from "./pigeon";
import { FoodSystem } from "./items";
import { HazardSystem } from "./hazards";
import { InputController } from "./input";
import { Sfx } from "./audio";

export type RunEndReason = "starved" | "car" | "abandoned";

export interface RunResult {
  reason: RunEndReason;
  score: number;
  durationMs: number;
  itemsCollected: number;
  seed: string;
  itemsEaten: readonly FoodType[];
}

export interface GameRunHandlers {
  onRunEnd(result: RunResult): void;
  onPauseRequest(): void;
  onFirstInteraction(): void;
}

interface PortalInstance {
  kind: "exit" | "return";
  group: THREE.Group;
  ring: THREE.Mesh;
  center: THREE.Vector3;
  baseY: number;
  radius: number;
  halfHeight: number;
  spinSpeed: number;
  phase: number;
}

const EXIT_PORTAL_CENTER = new THREE.Vector3(0, 3.3, 0);
const RETURN_PORTAL_CENTER = new THREE.Vector3(PIGEON_SPAWN.x, 1.4, PIGEON_SPAWN.z + 1.2);
const PORTAL_RADIUS = 1.04;
const PORTAL_HALF_HEIGHT = 1.04;
const PORTAL_REDIRECT_COOLDOWN_MS = 1200;
const KID_DONUT_STEAL_GRACE_MS = 500;
const PARROT_DIALOGUE_NEAR_RADIUS = 2.4;
const PARROT_INTRO_LINE =
  "Eat up kid or you'll never survive in this town! Come back and see me when you're bigger.";
const PARROT_REPEAT_LINE = "Come back and see me when you're bigger.";
const PARROT_CHALLENGE_LINE =
  "Okay kid, you've got some potential. Go find the old woman feeding the birds and figure out how to get to the breadcrumbs before the sparrows do.";
const DONUT_HEIST_PROMPT_LINE =
  "Good job. Now there's a little brat in the park with a donut. Grab his donut as you fly by him (press and hold E while flying to grab and hold food). He'll chase you so take it to a rooftop to eat it. Go get that donut!";
const POLLY_CHALLENGE_PROMPT_LINE =
  "Not bad kid. Can you figure out how to meet me on the highest roof? Remember that shift helps you move faster.";
const GRANDMA_CHALLENGE_TITLE = "Challenge: Grandma the bird feeder";
const GRANDMA_CHALLENGE_GOAL = 8;
const GRANDMA_CHALLENGE_BONUS_POINTS = 50;
const DONUT_HEIST_TITLE = "Challenge: Donut Heist";
const DONUT_HEIST_GOAL = 1;
const DONUT_HEIST_BONUS_POINTS = 100;
const DONUT_HEIST_MIN_ROOFTOP_Y = 1.0;
const POLLY_CHALLENGE_TITLE = "Challenge: Meet Polly on highest roof";
const POLLY_CHALLENGE_GOAL = 1;
const POLLY_CHALLENGE_BONUS_POINTS = 250;
const POLLY_POPCORN_OFFSET = new THREE.Vector3(1, 0, 0);
const POLLY_CHALLENGE_SUCCESS_LINE =
  "Great work kid, you beat the game! Record your name on the leaderboard. I have no more challenges for you but I can recommend flying through the portal above the fountain to try out another game.";
// TEMP TEST HACKS: disable when done testing.
const DEBUG_START_AT_LEVEL_5 = false;
const DEBUG_DISABLE_HUNGER_DRAIN = false;
///////////////////////////////////////////////
const BREADCRUMB_TYPE = FOOD_TYPES.find((type) => type.id === "breadcrumb") ?? FOOD_TYPES[0];
const DONUT_TYPE = FOOD_TYPES.find((type) => type.id === "donut") ?? FOOD_TYPES[0];
const POPCORN_TYPE = FOOD_TYPES.find((type) => type.id === "popcorn") ?? FOOD_TYPES[0];
const ALLOWED_PORTAL_QUERY_KEYS = [
  "portal",
  "username",
  "color",
  "speed",
  "ref",
  "avatar_url",
  "team",
  "hp",
  "speed_x",
  "speed_y",
  "speed_z",
  "rotation_x",
  "rotation_y",
  "rotation_z",
] as const;

/**
 * Owns a single run: scene, world, pigeon, hazards, loop. Call `start()` to
 * begin a run and `stop()` to cancel. Pausing is just toggling `paused`.
 */
export class Game {
  private readonly canvas: HTMLCanvasElement;
  private readonly hud: HudBinding;
  private readonly handlers: GameRunHandlers;
  private readonly input: InputController;
  private readonly sfx = new Sfx();

  private bundle: SceneBundle | null = null;
  private pigeon: Pigeon | null = null;
  private world: WorldBuildResult | null = null;
  private food: FoodSystem | null = null;
  private hazards: HazardSystem | null = null;
  private transport: LocalTransport | null = null;

  private running = false;
  private started = false;  // true once the player first locks the pointer
  paused = false;
  private useThirdPerson = true;
  private runStartedAt = 0;
  private elapsedMs = 0;

  private score = 0;
  private hunger = HUNGER_MAX;
  private itemsEaten: FoodType[] = [];
  private eventBanner = "";
  private eventBannerUntil = 0;
  private eventBannerFood: FoodType | null = null;
  private eventBannerIcon: string | null = null;
  private eventBannerSeq = 0;
  private xp = 0;
  private level = 1;

  private accumulator = 0;
  private lastFrame = 0;
  private tick = 0;
  private rafId = 0;
  private seed = "";
  private resizeObserver: ResizeObserver | null = null;
  private portals: PortalInstance[] = [];
  private portalRedirecting = false;
  private portalLastTriggerAt = 0;
  private kidDonutStealGraceUntil = 0;
  private parrotWasNear = false;
  private parrotIntroSpoken = false;
  private parrotWalkedAwayAfterIntro = false;
  private pendingChallengeChoice: "grandma" | "donutHeist" | "pollyRoof" | null = null;
  private grandmaChallengeActive = false;
  private grandmaBreadcrumbsEaten = 0;
  private grandmaChallengeCompleted = false;
  private donutHeistActive = false;
  private donutHeistCompleted = false;
  private donutHeistProgress = 0;
  private donutHeistGrabbedDonut = false;
  private pollyChallengeActive = false;
  private pollyChallengeCompleted = false;
  private pollyChallengeOffered = false;
  private grandmaChallengeFlashSeq = 0;
  private challengeCompletionFood: FoodType = BREADCRUMB_TYPE;
  private challengeCompletionPoints = GRANDMA_CHALLENGE_BONUS_POINTS;
  private donutHeistOffered = false;

  constructor(canvas: HTMLCanvasElement, hud: HudBinding, handlers: GameRunHandlers) {
    this.canvas = canvas;
    this.hud = hud;
    this.handlers = handlers;
    this.input = new InputController(canvas, {
      onRequestPause: () => this.handlers.onPauseRequest(),
      onToggleCamera: () => { this.useThirdPerson = !this.useThirdPerson; },
      onFirstInteraction: () => {
        if (!this.started) this.started = true;
        this.handlers.onFirstInteraction();
      },
      onPointerLockChange: (locked) => {
        this.hud.setPointerLocked(locked);
        if (locked && !this.started) this.started = true;
      },
    });
    this.hud.onChallengeChoice((accepted) => this.handleParrotChallengeChoice(accepted));
  }

  start(seed = makeSeed()): void {
    this.stop();
    this.seed = seed;
    const rng = seededRng(seed);

    const bundle = createScene(this.canvas);
    const world = buildWorld(bundle.scene, rng);
    const pigeon = new Pigeon();
    bundle.scene.add(pigeon.group);
    const food = new FoodSystem(bundle.scene, world.foodSpawns, rng, world.rooftopFoodSpawns, world.colliders);
    const hazards = new HazardSystem(bundle.scene, world, rng);

    this.bundle = bundle;
    this.world = world;
    this.pigeon = pigeon;
    this.food = food;
    this.hazards = hazards;
    this.transport = new LocalTransport();

    // The FP "view-beak" is mounted as a child of the FP camera so it follows
    // the view without any per-frame transform tracking. Adding the camera to
    // the scene is required for THREE to traverse & render its children.
    bundle.scene.add(bundle.firstPersonCamera);
    bundle.firstPersonCamera.add(pigeon.viewBeak);
    this.setupPortals(bundle.scene);

    this.score = 0;
    this.hunger = HUNGER_MAX;
    this.itemsEaten = [];
    this.eventBanner = "";
    this.eventBannerUntil = 0;
    this.eventBannerFood = null;
    this.eventBannerIcon = null;
    this.xp = 0;
    this.level = 1;
    this.kidDonutStealGraceUntil = 0;
    this.parrotWasNear = false;
    this.parrotIntroSpoken = false;
    this.parrotWalkedAwayAfterIntro = false;
    this.pendingChallengeChoice = null;
    this.grandmaChallengeActive = false;
    this.grandmaBreadcrumbsEaten = 0;
    this.grandmaChallengeCompleted = false;
    this.donutHeistActive = false;
    this.donutHeistCompleted = false;
    this.donutHeistProgress = 0;
    this.donutHeistGrabbedDonut = false;
    this.pollyChallengeActive = false;
    this.pollyChallengeCompleted = false;
    this.pollyChallengeOffered = false;
    this.grandmaChallengeFlashSeq = 0;
    this.challengeCompletionFood = BREADCRUMB_TYPE;
    this.challengeCompletionPoints = GRANDMA_CHALLENGE_BONUS_POINTS;
    this.donutHeistOffered = false;
    this.hud.showChallengeDialog(false);
    this.accumulator = 0;
    this.tick = 0;
    this.lastFrame = performance.now();
    this.runStartedAt = this.lastFrame;
    this.elapsedMs = 0;
    this.paused = false;
    this.started = false;
    this.running = true;

    if (DEBUG_START_AT_LEVEL_5) {
      this.applyDebugStartLevel(pigeon, 5);
    }

    this.resizeObserver = new ResizeObserver(() => bundle.resize());
    this.resizeObserver.observe(this.canvas);
    bundle.resize();

    this.input.resetLook();
    this.input.resetFirstInteraction();
    this.sfx.ensureReady();

    this.publishHud();
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    this.input.releasePointerLock();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.bundle) {
      this.bundle.scene.clear();
      this.bundle.dispose();
      this.bundle = null;
    }
    this.pigeon = null;
    this.world = null;
    this.food = null;
    this.hazards = null;
    this.transport?.close();
    this.transport = null;
  }

  pause(): void {
    if (!this.running) return;
    this.paused = true;
    this.input.releasePointerLock();
  }

  resume(): void {
    if (!this.running) return;
    this.paused = false;
    this.lastFrame = performance.now();
    this.input.requestPointerLock();
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.frame);

    if (this.paused) {
      this.lastFrame = now;
      return;
    }

    const frameDt = Math.min(MAX_FRAME_DT, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    this.accumulator += frameDt;
    this.elapsedMs = now - this.runStartedAt;

    while (this.accumulator >= FIXED_DT) {
      this.step(FIXED_DT);
      this.accumulator -= FIXED_DT;
    }

    this.render();
    this.publishHud();
  };

  private step(dt: number): void {
    const { pigeon, world, food, hazards, transport } = this;
    if (!pigeon || !world || !food || !hazards || !transport) return;

    const input = this.input.consumeFrame(++this.tick);
    transport.sendInput(input);

    pigeon.step(dt, input, world.colliders);

    if (pigeon.grounded) {
      const released = food.releaseHeldIfAny({ position: pigeon.position, yaw: input.yaw });
      if (released) this.sfx.peck();
    } else if (!input.peckHeld) {
      const released = food.releaseHeldIfAny();
      if (released) this.sfx.peck();
    }

    if (input.peck) {
      if (!pigeon.grounded) {
        let grabbed = food.tryGrabAirborne(pigeon.position);
        if (!grabbed && hazards.tryTakeKidDonut(pigeon.position)) {
          grabbed = food.spawnHeldFood("donut", pigeon.position, "kidDonut");
          if (grabbed) this.kidDonutStealGraceUntil = performance.now() + KID_DONUT_STEAL_GRACE_MS;
          if (grabbed && this.donutHeistActive) this.donutHeistGrabbedDonut = true;
        }
        this.sfx.peck();
        if (grabbed) this.banner(`grabbed ${grabbed.name}`, 0.9);
      } else {
        const pecked = food.tryPeck(pigeon.position, input.yaw);
        if (pecked) {
          this.hunger = Math.min(HUNGER_MAX, this.hunger + pecked.hungerGain);
          if (pecked.fullyConsumed) {
            this.score += pecked.type.points;
            this.itemsEaten.push(pecked.type);
            this.bannerFood(pecked.type, `+${pecked.type.points} ${pecked.type.name}`, 1.35);
            this.sfx.pickup();
            this.awardXp(pecked.type.points * XP_PER_FOOD_POINT, pigeon);
            if (this.grandmaChallengeActive && pecked.type.id === "breadcrumb" && pecked.source === "birdFeeder") {
              this.grandmaBreadcrumbsEaten = Math.min(
                GRANDMA_CHALLENGE_GOAL,
                this.grandmaBreadcrumbsEaten + 1,
              );
              if (this.grandmaBreadcrumbsEaten >= GRANDMA_CHALLENGE_GOAL) {
                this.grandmaChallengeActive = false;
                this.grandmaChallengeCompleted = true;
                this.score += GRANDMA_CHALLENGE_BONUS_POINTS;
                this.hunger = HUNGER_MAX;
                this.challengeCompletionFood = BREADCRUMB_TYPE;
                this.challengeCompletionPoints = GRANDMA_CHALLENGE_BONUS_POINTS;
                this.grandmaChallengeFlashSeq += 1;
              }
            }
            if (
              this.donutHeistActive &&
              pecked.type.id === "donut" &&
              pecked.source === "kidDonut" &&
              pigeon.position.y >= DONUT_HEIST_MIN_ROOFTOP_Y
            ) {
              this.donutHeistActive = false;
              this.donutHeistCompleted = true;
              this.donutHeistProgress = DONUT_HEIST_GOAL;
              this.score += DONUT_HEIST_BONUS_POINTS;
              this.hunger = HUNGER_MAX;
              this.challengeCompletionFood = DONUT_TYPE;
              this.challengeCompletionPoints = DONUT_HEIST_BONUS_POINTS;
              this.grandmaChallengeFlashSeq += 1;
            }
          } else {
            this.sfx.peck();
          }
        } else {
          this.sfx.peck();
        }
      }
    }
    if (input.flap && pigeon.grounded === false && Math.abs(pigeon.velocity.y) > 0.1) {
      this.sfx.flap();
    }

    // Continuous XP: airtime and active sprinting both count.
    if (!pigeon.grounded) this.awardXp(XP_PER_FLY_SECOND * dt, pigeon);
    const moving = input.move.x !== 0 || input.move.z !== 0;
    if (input.sprint && moving && pigeon.grounded) {
      this.awardXp(XP_PER_SPRINT_SECOND * dt, pigeon);
    }

    food.update(dt, performance.now(), pigeon.position);

    const hazardEvents = hazards.step(dt, pigeon.position, input.peck);
    for (const evt of hazardEvents) {
      if (evt.type === "shoo") {
        this.hunger = Math.max(0, this.hunger - SHOO_HUNGER_PENALTY);
        this.banner("shoo!", 0.8);
        this.sfx.shoo();
      } else if (evt.type === "bump") {
        this.hunger = Math.max(0, this.hunger - SHOO_HUNGER_PENALTY);
        this.banner("Ouch!", 0.7);
        this.sfx.shoo();
      } else if (evt.type === "familyCatch") {
        if (performance.now() < this.kidDonutStealGraceUntil) continue;
        const dropped = food.releaseHeldIfAny();
        if (dropped?.id === "donut") {
          const picked = food.consumeNearestFoodByType(evt.position, "donut", 1.5);
          if (picked) hazards.restoreKidDonutAt(evt.position);
        }
      } else if (evt.type === "car") {
        this.sfx.gameOver();
        this.endRun("car");
        return;
      } else if (evt.type === "breadcrumbs") {
        food.spawnBreadcrumbs(evt.position, evt.count, "birdFeeder");
      } else if (evt.type === "scavenge") {
        food.consumeBreadcrumbsNear(evt.position, evt.radius, evt.maxCount);
      }
    }

    if (this.started && !DEBUG_DISABLE_HUNGER_DRAIN) {
      this.hunger -= HUNGER_DRAIN_PER_SECOND * dt;
      if (this.hunger <= 0) {
        this.hunger = 0;
        this.sfx.gameOver();
        this.endRun("starved");
        return;
      }
    }

    if (this.eventBannerUntil > 0 && performance.now() > this.eventBannerUntil) {
      this.eventBanner = "";
      this.eventBannerUntil = 0;
      this.eventBannerFood = null;
      this.eventBannerIcon = null;
    }

    this.updateParrotDialogue(pigeon.position);

    if (this.pollyChallengeActive && this.world && pigeon.grounded) {
      const roofCenter = this.world.pollyRoofCenter;
      const dx = pigeon.position.x - roofCenter.x;
      const dz = pigeon.position.z - roofCenter.z;
      const onPollyRoofCenter = dx * dx + dz * dz <= 2.2 * 2.2;
      const nearRoofHeight = Math.abs(pigeon.position.y - roofCenter.y) <= 0.8;
      if (onPollyRoofCenter && nearRoofHeight) {
        this.pollyChallengeActive = false;
        this.pollyChallengeCompleted = true;
        this.score += POLLY_CHALLENGE_BONUS_POINTS;
        this.hunger = HUNGER_MAX;
        this.itemsEaten.push(POPCORN_TYPE);
        this.challengeCompletionFood = POPCORN_TYPE;
        this.challengeCompletionPoints = POLLY_CHALLENGE_BONUS_POINTS;
        this.grandmaChallengeFlashSeq += 1;
        this.pollySay(POLLY_CHALLENGE_SUCCESS_LINE, 12);
      }
    }

    transport.emitState({ tick: this.tick });
  }

  private render(): void {
    const { bundle, pigeon } = this;
    if (!bundle || !pigeon) return;
    this.updatePortals(performance.now(), pigeon);

    pigeon.group.visible = this.useThirdPerson;
    pigeon.updateViewBeak(!this.useThirdPerson, pigeon.peckProgress);

    if (this.useThirdPerson) {
      const cam = bundle.thirdPersonCamera;
      const yaw = this.input.getYaw();
      const pitch = this.input.getPitch();
      const offset = new THREE.Vector3(
        Math.sin(yaw) * 3,
        1.2 - pitch * 0.8,
        Math.cos(yaw) * 3,
      );
      cam.position.copy(pigeon.position).add(offset);
      cam.lookAt(pigeon.position.x, pigeon.position.y + 0.1, pigeon.position.z);
      bundle.renderer.render(bundle.scene, cam);
    } else {
      const cam = bundle.firstPersonCamera;
      const walking = pigeon.grounded && pigeon.velocity.lengthSq() > 0.5;
      const bob = walking ? Math.sin(performance.now() * 0.012) * 0.015 : 0;
      const yaw = this.input.getYaw();
      // GoPro mount: camera sits on top of the pigeon's head so you see your
      // own beak poking into the bottom of the frame.
      pigeon.getHeadMount(yaw, cam.position);
      cam.position.y += bob;
      cam.rotation.order = "YXZ";
      cam.rotation.y = yaw;
      cam.rotation.x = this.input.getPitch();
      bundle.renderer.render(bundle.scene, cam);
    }
  }

  private banner(text: string, seconds: number): void {
    this.eventBanner = text;
    this.eventBannerUntil = performance.now() + seconds * 1000;
    this.eventBannerFood = null;
    this.eventBannerIcon = null;
    this.eventBannerSeq += 1;
  }

  private bannerFood(type: FoodType, text: string, seconds: number): void {
    this.eventBanner = text;
    this.eventBannerUntil = performance.now() + seconds * 1000;
    this.eventBannerFood = type;
    this.eventBannerIcon = null;
    this.eventBannerSeq += 1;
  }

  private bannerIcon(icon: string, text: string, seconds: number): void {
    this.eventBanner = text;
    this.eventBannerUntil = performance.now() + seconds * 1000;
    this.eventBannerFood = null;
    this.eventBannerIcon = icon;
    this.eventBannerSeq += 1;
  }

  private pollySay(text: string, seconds: number): void {
    this.bannerIcon("🦜", text, seconds);
    this.pigeon && this.hazards?.setPirateParrotSpeakingTo(this.pigeon.position, Math.max(seconds, 1.6));
  }

  private pollySayUntilChoice(text: string): void {
    this.bannerIcon("🦜", text, 0);
    this.eventBannerUntil = Number.POSITIVE_INFINITY;
    this.pigeon && this.hazards?.setPirateParrotSpeakingTo(this.pigeon.position, 8);
  }

  private pollySayDonutHeistUntilChoice(text: string): void {
    this.pollySayUntilChoice(text);
  }

  private pollySayBreadcrumbChallenge(text: string, seconds: number): void {
    this.bannerFood(BREADCRUMB_TYPE, `${text}`, seconds);
    this.pigeon && this.hazards?.setPirateParrotSpeakingTo(this.pigeon.position, Math.max(seconds, 1.6));
  }

  private pollySayBreadcrumbChallengeUntilChoice(text: string): void {
    this.bannerFood(BREADCRUMB_TYPE, `${text}`, 0);
    this.eventBannerUntil = Number.POSITIVE_INFINITY;
    this.pigeon && this.hazards?.setPirateParrotSpeakingTo(this.pigeon.position, 8);
  }

  private awardXp(amount: number, pigeon: Pigeon): void {
    if (amount <= 0 || this.level >= MAX_LEVEL) return;
    this.xp += amount;
    while (
      this.level < MAX_LEVEL &&
      this.xp >= (LEVEL_XP_REQUIREMENTS[this.level - 1] ?? Infinity)
    ) {
      this.level += 1;
      pigeon.staminaMax += STAMINA_PER_LEVEL;
      pigeon.stamina = pigeon.staminaMax;
      pigeon.speedBonus += SPEED_PER_LEVEL;
      pigeon.setLevel(this.level);
      this.bannerIcon("🪽", `Level ${this.level}! faster + more stamina`, 2);
      this.sfx.levelUp();
    }
  }

  private applyDebugStartLevel(pigeon: Pigeon, targetLevel: number): void {
    const clamped = Math.max(1, Math.min(MAX_LEVEL, targetLevel));
    if (clamped <= this.level) return;
    for (let lvl = this.level + 1; lvl <= clamped; lvl++) {
      pigeon.staminaMax += STAMINA_PER_LEVEL;
      pigeon.speedBonus += SPEED_PER_LEVEL;
    }
    this.level = clamped;
    pigeon.stamina = pigeon.staminaMax;
    pigeon.setLevel(this.level);
  }

  private updateParrotDialogue(pigeonPos: THREE.Vector3): void {
    const parrotPos = this.hazards?.getPirateParrotPosition();
    if (!parrotPos) return;
    const dx = pigeonPos.x - parrotPos.x;
    const dz = pigeonPos.z - parrotPos.z;
    const d2 = dx * dx + dz * dz;
    const nearRadius2 = PARROT_DIALOGUE_NEAR_RADIUS ** 2;
    const isNear = d2 <= nearRadius2;

    if (isNear && !this.parrotWasNear) {
      if (
        this.donutHeistCompleted &&
        !this.pollyChallengeActive &&
        !this.pollyChallengeCompleted &&
        !this.pollyChallengeOffered
      ) {
        this.pollySayUntilChoice(POLLY_CHALLENGE_PROMPT_LINE);
        this.pendingChallengeChoice = "pollyRoof";
        this.hud.showChallengeDialog(true, "polly");
        this.pollyChallengeOffered = true;
      } else if (
        this.grandmaChallengeCompleted &&
        !this.donutHeistActive &&
        !this.donutHeistCompleted &&
        !this.donutHeistOffered
      ) {
        this.pollySayDonutHeistUntilChoice(DONUT_HEIST_PROMPT_LINE);
        this.pendingChallengeChoice = "donutHeist";
        this.hud.showChallengeDialog(true, "donutHeist");
        this.donutHeistOffered = true;
      } else if (
        this.level >= 2 &&
        !this.grandmaChallengeActive &&
        !this.grandmaChallengeCompleted &&
        this.grandmaBreadcrumbsEaten < GRANDMA_CHALLENGE_GOAL
      ) {
        this.pollySayBreadcrumbChallengeUntilChoice(PARROT_CHALLENGE_LINE);
        this.parrotIntroSpoken = true;
        this.parrotWalkedAwayAfterIntro = false;
        this.pendingChallengeChoice = "grandma";
        this.hud.showChallengeDialog(true, "grandma");
      } else if (!this.parrotIntroSpoken) {
        this.pollySay(PARROT_INTRO_LINE, 7.2);
        this.parrotIntroSpoken = true;
        this.parrotWalkedAwayAfterIntro = false;
      } else if (this.parrotWalkedAwayAfterIntro && this.level <= 2) {
        this.pollySay(PARROT_REPEAT_LINE, 4.5);
        this.parrotWalkedAwayAfterIntro = false;
      }
    }

    // Mark "walked away" as soon as the player exits the near talk radius.
    if (this.parrotIntroSpoken && !isNear && this.parrotWasNear) {
      this.parrotWalkedAwayAfterIntro = true;
    }

    this.parrotWasNear = isNear;
  }

  private handleParrotChallengeChoice(accepted: boolean): void {
    if (!this.pendingChallengeChoice) return;
    const chosen = this.pendingChallengeChoice;
    this.pendingChallengeChoice = null;
    this.hud.showChallengeDialog(false);
    if (!accepted) {
      if (chosen === "donutHeist") this.donutHeistOffered = false;
      if (chosen === "pollyRoof") this.pollyChallengeOffered = false;
      this.pollySay("Maybe later, kid.", 1.8);
      return;
    }
    if (chosen === "grandma") {
      this.grandmaChallengeActive = true;
      this.grandmaBreadcrumbsEaten = 0;
      this.pollySayBreadcrumbChallenge("Race the sparrows to 8 grandma breadcrumbs!", 2.1);
      return;
    }
    if (chosen === "donutHeist") {
      this.donutHeistActive = true;
      this.donutHeistProgress = 0;
      this.donutHeistGrabbedDonut = false;
      this.pollySay("Donut Heist is on. Rooftops are your friend.", 2.2);
      return;
    }
    this.pollyChallengeActive = true;
    if (this.world) {
      const roofCenter = this.world.pollyRoofCenter;
      const popcornSpawn = roofCenter.clone().add(POLLY_POPCORN_OFFSET);
      this.food?.consumeNearestFood(popcornSpawn, 2.2);
      this.food?.spawnFoodAt("popcorn", popcornSpawn, "ambient");
      this.hazards?.flyPirateParrotTo(new THREE.Vector3(roofCenter.x, roofCenter.y + 0.04, roofCenter.z));
    }
    this.pollySay("Polly Challenge accepted. Find the highest roof.", 2.2);
  }

  private setupPortals(scene: THREE.Scene): void {
    this.portals = [];
    this.portalRedirecting = false;
    this.portalLastTriggerAt = 0;

    this.portals.push(this.createPortal(scene, "exit", EXIT_PORTAL_CENTER, 0x33ff88, "Vibe Jam Portal"));
    const incomingRef = this.getAllowedPortalParams().get("ref");
    const arrivedViaPortal = this.getAllowedPortalParams().get("portal") === "true";
    if (arrivedViaPortal && incomingRef) {
      this.portals.push(this.createPortal(scene, "return", RETURN_PORTAL_CENTER, 0xff6666, "Return Portal"));
    }
  }

  private createPortal(
    scene: THREE.Scene,
    kind: "exit" | "return",
    center: THREE.Vector3,
    color: number,
    label: string,
  ): PortalInstance {
    const group = new THREE.Group();
    group.position.copy(center);

    const ringMat = new THREE.MeshPhongMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.86,
    });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.9, 0.08, 16, 64), ringMat);
    ring.castShadow = true;
    group.add(ring);

    const inner = new THREE.Mesh(
      new THREE.CircleGeometry(0.83, 40),
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.34,
        side: THREE.DoubleSide,
      }),
    );
    group.add(inner);

    const glow = new THREE.PointLight(color, 0.45, 5);
    glow.position.set(0, 0, 0.2);
    group.add(glow);

    group.add(this.createPortalLabel(label, color));
    scene.add(group);

    return {
      kind,
      group,
      ring,
      center: center.clone(),
      baseY: center.y,
      radius: PORTAL_RADIUS,
      halfHeight: PORTAL_HALF_HEIGHT,
      spinSpeed: kind === "exit" ? 1.8 : 1.3,
      phase: Math.random() * Math.PI * 2,
    };
  }

  private createPortalLabel(text: string, color: number): THREE.Mesh {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#101418";
      ctx.globalAlpha = 0.45;
      ctx.fillRect(40, 28, 432, 72);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = `#${color.toString(16).padStart(6, "0")}`;
      ctx.lineWidth = 3;
      ctx.strokeRect(40, 28, 432, 72);
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 36px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 2);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1.25, 0.32),
      new THREE.MeshBasicMaterial({ map: texture, transparent: true, side: THREE.DoubleSide }),
    );
    mesh.position.set(0, 0, 0.02);
    return mesh;
  }

  private updatePortals(now: number, pigeon: Pigeon): void {
    for (const portal of this.portals) {
      portal.ring.rotation.z += 0.02 * portal.spinSpeed;
      portal.group.position.y = portal.baseY + Math.sin(now * 0.002 + portal.phase) * 0.03;
      portal.center.y = portal.group.position.y;
    }

    if (this.portalRedirecting || now - this.portalLastTriggerAt < PORTAL_REDIRECT_COOLDOWN_MS) return;

    for (const portal of this.portals) {
      if (!this.isPigeonInsidePortal(pigeon.position, portal)) continue;
      this.portalLastTriggerAt = now;
      if (portal.kind === "exit") this.redirectToNextGame();
      else this.redirectBackToRef();
      return;
    }
  }

  private isPigeonInsidePortal(position: THREE.Vector3, portal: PortalInstance): boolean {
    const dx = position.x - portal.center.x;
    const dz = position.z - portal.center.z;
    if (Math.hypot(dx, dz) > portal.radius) return false;
    return Math.abs(position.y - portal.center.y) <= portal.halfHeight;
  }

  private getAllowedPortalParams(): URLSearchParams {
    const input = new URLSearchParams(window.location.search);
    const out = new URLSearchParams();
    for (const key of ALLOWED_PORTAL_QUERY_KEYS) {
      const value = input.get(key);
      if (value !== null) out.set(key, value);
    }
    return out;
  }

  private redirectToNextGame(): void {
    const params = this.getAllowedPortalParams();
    params.set("portal", "true");
    params.set("ref", window.location.hostname);
    this.redirectToUrl(`https://vibej.am/portal/2026?${params.toString()}`);
  }

  private redirectBackToRef(): void {
    const incomingRef = this.getAllowedPortalParams().get("ref");
    if (!incomingRef) return;

    const params = this.getAllowedPortalParams();
    params.set("portal", "true");
    params.set("ref", window.location.hostname);

    const base = /^https?:\/\//i.test(incomingRef) ? incomingRef : `https://${incomingRef}`;
    try {
      const target = new URL(base);
      target.search = params.toString();
      this.redirectToUrl(target.toString());
    } catch {
      console.warn("invalid return ref URL", incomingRef);
    }
  }

  private redirectToUrl(url: string): void {
    this.portalRedirecting = true;
    window.location.href = url;
  }

  private publishHud(): void {
    if (!this.pigeon) return;
    const activeChallengeTitle = this.grandmaChallengeActive
      ? GRANDMA_CHALLENGE_TITLE
      : this.donutHeistActive
        ? DONUT_HEIST_TITLE
        : POLLY_CHALLENGE_TITLE;
    const activeChallengeProgress = this.grandmaChallengeActive
      ? this.grandmaBreadcrumbsEaten
      : this.donutHeistActive
        ? this.donutHeistProgress
        : 0;
    const activeChallengeGoal = this.grandmaChallengeActive
      ? GRANDMA_CHALLENGE_GOAL
      : this.donutHeistActive
        ? DONUT_HEIST_GOAL
        : POLLY_CHALLENGE_GOAL;
    this.hud.updateGameState({
      score: this.score,
      hunger: Math.round(this.hunger),
      stamina: Math.round(this.pigeon.stamina),
      staminaMax: Math.round(this.pigeon.staminaMax),
      level: this.level,
      airborne: !this.pigeon.grounded,
      elapsedMs: Math.floor(this.elapsedMs),
      items: this.itemsEaten.map((t) => ({ id: t.id, name: t.name })),
      thirdPerson: this.useThirdPerson,
      lastEvent: this.eventBanner,
      lastEventFood: this.eventBannerFood
        ? {
            id: this.eventBannerFood.id,
            name: this.eventBannerFood.name,
            color: this.eventBannerFood.color,
          }
        : null,
      lastEventIcon: this.eventBannerIcon,
      eventBannerSeq: this.eventBannerSeq,
      eventBannerPinned: !Number.isFinite(this.eventBannerUntil),
      challengeActive: this.grandmaChallengeActive || this.donutHeistActive || this.pollyChallengeActive,
      challengeTitle: activeChallengeTitle,
      challengeProgress: activeChallengeProgress,
      challengeGoal: activeChallengeGoal,
      challengeChecklist: this.donutHeistActive
        ? {
            item1Label: "grab kid's donut (peck + hold while flying to grab)",
            item1Done: this.donutHeistGrabbedDonut,
            item2Label: "eat donut on rooftop",
            item2Done: this.donutHeistCompleted,
          }
        : this.pollyChallengeActive
          ? {
              item1Label: "meet Polly on the highest roof (hold shift to fly faster)",
              item1Done: false,
            }
        : null,
      challengeFlashSeq: this.grandmaChallengeFlashSeq,
      challengeCompletionFood: {
        id: this.challengeCompletionFood.id,
        name: this.challengeCompletionFood.name,
        color: this.challengeCompletionFood.color,
      },
      challengeCompletionPoints: this.challengeCompletionPoints,
    });
  }

  private endRun(reason: RunEndReason): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
    this.input.releasePointerLock();
    const durationMs = Math.floor(this.elapsedMs);
    const itemsCollected = this.itemsEaten.length;
    const result: RunResult = {
      reason,
      score: this.score,
      durationMs: Math.max(1000, durationMs),
      itemsCollected,
      seed: this.seed,
      itemsEaten: this.itemsEaten,
    };
    this.handlers.onRunEnd(result);
  }
}
